class ManualSearch extends SearchForm {
  constructor() {
    super();

    this.ACTIVE_MARK_CLASS = "mark--active";

    this.currentMarkIndex = -1;
  }

  /**
   * Adds event listeners for input changes, keydown events, and navigation button clicks.
   */
  addListeners() {
    super.addListeners();

    this.input.addEventListener("keydown", event => event.key === "Enter" && this.handleKeyListener(event));
    this.navigationButtons.forEach(button => {
      button.addEventListener("click", event => this.handleButtonClick(event));
    });
  }

  /**
   * Handles keydown events for navigating through marks.
   * @param event
   */
  handleKeyListener(event) {
    event.preventDefault();

    const delta = event.shiftKey ? -1 : 1;

    this.focusMark(this.currentMarkIndex + delta);
  }

  /**
   * Handles navigation button clicks to move to the next or previous mark.
   * @param event
   */
  handleButtonClick(event) {
    event.preventDefault();

    const isNext = event.currentTarget.dataset.searchNext !== undefined;
    const delta = isNext ? 1 : -1;

    this.focusMark(this.currentMarkIndex + delta);
  }

  /**
   * Handles input changes, highlights matches, and clears highlights if input is empty.
   */
  onChange() {
    super.onChange();

    const term = this.input.value.trim();

    if (!term) return this.resetMarks();

    this.highlightMatches(term);

    this.currentMarkIndex = -1;

    if (this.marks.length) this.focusMark(0);
  }

  /**
   * Resets all marks and clears highlights.
   */
  resetMarks() {
    this.clearHighlights();

    this.currentMarkIndex = -1;
  }

  /**
   * Cleans up all existing highlights in the manual content.
   */
  clearHighlights() {
    const content = this.manualContent;

    if (!content) return;

    if (this._originalHTML) {
      content.innerHTML = this._originalHTML;

      this._originalHTML = null;
    }

    const marks = content.querySelectorAll("mark");

    marks.forEach(mark => {
      while (mark.firstChild) mark.parentNode.insertBefore(mark.firstChild, mark);

      mark.parentNode.removeChild(mark);
    });

    this.currentMarkIndex = -1;
  }

  /**
   * Highlights all matches of the search term in the manual content.
   * @param term
   */
  highlightMatches(term) {
    const content = this.manualContent;

    if (!content || !term) return;

    this.clearHighlights();

    if (!this._originalHTML) {
      this._originalHTML = content.innerHTML;
    }

    const regexSource = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(regexSource, "gi");

    this.walkNode(content, regex);
  }

  /**
   * Recursively walks through the DOM nodes to find and highlight matches.
   * @param node
   * @param regex
   */
  walkNode(node, regex) {
    if (node.nodeType === Node.TEXT_NODE) {
      this._highlightTextNode(node, regex);
    } else if (node.nodeType === Node.ELEMENT_NODE && node.childNodes) {
      this._walkElementNode(node, regex);
    }
  }

  /**
   * Highlights matches in a text node.
   * @param {Node} node
   * @param {RegExp} regex
   */
  _highlightTextNode(node, regex) {
    regex.lastIndex = 0;

    const text = node.textContent;
    const frag = document.createDocumentFragment();

    let lastIndex = 0;
    let match;

    while ((match = regex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        frag.appendChild(this._createTextNodeSlice(text, lastIndex, match.index));
      }

      frag.appendChild(this._createMark(match[0]));

      lastIndex = regex.lastIndex;
    }

    if (lastIndex < text.length) {
      frag.appendChild(this._createTextNodeSlice(text, lastIndex));
    }

    if (frag.childNodes.length) {
      node.parentNode.replaceChild(frag, node);
    }
  }

  /**
   * Creates a text node from a slice of the given text.
   * If end is undefined, slices to the end of the string.
   * @param {string} text
   * @param {number} start
   * @param {number} [end]
   * @returns {Text}
   */
  _createTextNodeSlice(text, start, end) {
    return document.createTextNode(end !== undefined ? text.slice(start, end) : text.slice(start));
  }

  /**
   * Recursively walks through child nodes of an element node.
   * @param {Node} node
   * @param {RegExp} regex
   */
  _walkElementNode(node, regex) {
    Array.from(node.childNodes).forEach(child => this.walkNode(child, regex));
  }

  /**
   * Creates a <mark> element for a matched string.
   * @param {string} text
   * @returns {Element}
   */
  _createMark(text) {
    const mark = document.createElement("mark");

    mark.classList.add("mark");

    mark.textContent = text;

    return mark;
  }

  /**
   * Focuses on the mark at the given index, scrolling it into view and highlighting it.
   * @param index
   */
  focusMark(index) {
    const marks = this.marks;

    if (!marks.length) return;

    this.currentMarkIndex = (index + marks.length) % marks.length;

    const mark = marks[this.currentMarkIndex];

    mark.scrollIntoView({ behavior: "smooth", block: "center" });
    mark.classList.add(this.ACTIVE_MARK_CLASS);

    marks.forEach((mark, index) => {
      if (index !== this.currentMarkIndex) mark.classList.remove(this.ACTIVE_MARK_CLASS);
    });
  }

  /**
   * Returns the manual content element.
   * @returns {Element}
   */
  get manualContent() {
    return document.querySelector("[data-manual-content]");
  }

  /**
   * Returns the navigation buttons elements.
   * @returns {NodeListOf<Element>}
   */
  get navigationButtons() {
    return this.querySelectorAll("[data-search-previous], [data-search-next]");
  }

  /**
   * Returns all current marks in the manual content.
   * @returns {Array<Element>}
   */
  get marks() {
    return Array.from(this.manualContent?.querySelectorAll("mark") || []);
  }
}

if (!customElements.get("manual-search")) {
  customElements.define("manual-search", ManualSearch);
}
